import React, { createContext, useState, useEffect, useContext } from "react";
import { getApiBaseUrl } from "../utils/apiConfig";

const ProjectContext = createContext();

export const useProjects = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${getApiBaseUrl()}/api/projects`;

  // Fetch all projects
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      setProjects(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get single project by slug
  const getProjectBySlug = async (slug) => {
    try {
      const response = await fetch(`${API_URL}/${slug}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error("Fetch project error:", err);
    }

    // Smart fallback: Check projects state by slug, ID, or title matching
    if (projects && projects.length > 0) {
      const cleanSlug = (slug || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const found = projects.find((p) => {
        if (!p) return false;
        const pSlug = (p.slug || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const pTitle = (p.title || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const pId = (p._id || "").toString();
        return (
          pSlug === cleanSlug ||
          pId === slug ||
          (cleanSlug.length > 2 && (pSlug.includes(cleanSlug) || cleanSlug.includes(pSlug))) ||
          (cleanSlug.length > 2 && (pTitle.includes(cleanSlug) || cleanSlug.includes(pTitle)))
        );
      });
      if (found) return found;
    }
    return null;
  };

  // Add new project
  const addProject = async (projectData) => {
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(projectData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to create project");
      }
      const newProject = await response.json();
      setProjects((prev) => [newProject, ...prev]);
      return newProject;
    } catch (err) {
      throw err;
    }
  };

  // Update project
  const updateProject = async (id, projectData) => {
    try {
      const response = await fetch(`${API_URL}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(projectData),
      });
      if (!response.ok) throw new Error("Failed to update project");
      const updatedProject = await response.json();
      setProjects((prev) =>
        prev.map((p) => (p._id === id ? updatedProject : p)),
      );
      return updatedProject;
    } catch (err) {
      throw err;
    }
  };

  // Delete project
  const deleteProject = async (id) => {
    try {
      const response = await fetch(`${API_URL}/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete project");
      setProjects((prev) => prev.filter((p) => p._id !== id));
    } catch (err) {
      throw err;
    }
  };

  // Upload image to Cloudinary via backend endpoint
  const uploadImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async (event) => {
        try {
          const response = await fetch(`${API_URL}/upload-image`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ image: event.target.result }),
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || "Failed to upload image to Cloudinary");
          }
          const data = await response.json();
          resolve(data.url);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        loading,
        error,
        fetchProjects,
        getProjectBySlug,
        addProject,
        updateProject,
        deleteProject,
        uploadImage,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
